// Display-only organizational tiers for the Tier Builder cheat sheet.
// PURELY presentational — a finer, expert-informed DEFAULT the user then
// customizes (move players, rename tiers, reorder). NEVER feeds getTierVal,
// generatePersonalTierSheet, usePricing, AI tier sheets, or batch sim.
// Keyed by posRank (from ADP) so it self-heals on every ADP refresh.
// Each tier: { tier, min, max, label }; min/max inclusive posRank bounds.

import { getEffectivePosRank, parseRankNum } from './adpData';

export const DISPLAY_TIER_MAPS = {
  QB: [
    { tier: 1, min: 1,  max: 1,   label: "Round 3" },
    { tier: 2, min: 2,  max: 4,   label: "Rounds 4-5" },
    { tier: 3, min: 5,  max: 8,   label: "Rounds 6-8" },
    { tier: 4, min: 9,  max: 11,  label: "Rounds 9-10" },
    { tier: 5, min: 12, max: 17,  label: "Rounds 11-13" },
    { tier: 6, min: 18, max: 21,  label: "Late Fliers" },
    { tier: 7, min: 22, max: 32,  label: "Superflex Options" },
    { tier: 8, min: 33, max: 999, label: "Deep Bench" },
  ],
  RB: [
    { tier: 1,  min: 1,  max: 2,   label: "Elite" },
    { tier: 2,  min: 3,  max: 5,   label: "Round 1" },
    { tier: 3,  min: 6,  max: 12,  label: "Round 2" },
    { tier: 4,  min: 13, max: 15,  label: "Round 3" },
    { tier: 5,  min: 16, max: 22,  label: "Rounds 4-5" },
    { tier: 6,  min: 23, max: 27,  label: "Rounds 5-6" },
    { tier: 7,  min: 28, max: 35,  label: "Rounds 7-9" },
    { tier: 8,  min: 36, max: 40,  label: "Rounds 9-10" },
    { tier: 9,  min: 41, max: 43,  label: "Rounds 10-11" },
    { tier: 10, min: 44, max: 51,  label: "Round 11+" },
    { tier: 11, min: 52, max: 60,  label: "Late Fliers" },
    { tier: 12, min: 61, max: 999, label: "Deep Bench" },
  ],
  WR: [
    { tier: 1,  min: 1,  max: 4,   label: "Early Round 1" },
    { tier: 2,  min: 5,  max: 6,   label: "Late Round 1" },
    { tier: 3,  min: 7,  max: 10,  label: "Round 2" },
    { tier: 4,  min: 11, max: 13,  label: "Early Round 3" },
    { tier: 5,  min: 14, max: 16,  label: "Late Round 3" },
    { tier: 6,  min: 17, max: 21,  label: "Early Round 4" },
    { tier: 7,  min: 22, max: 26,  label: "Late Rd 4-Early 5" },
    { tier: 8,  min: 27, max: 29,  label: "Late Rd 5-Rd 6" },
    { tier: 9,  min: 30, max: 39,  label: "Rounds 6-7" },
    { tier: 10, min: 40, max: 48,  label: "Late 7-Round 9" },
    { tier: 11, min: 49, max: 56,  label: "Round 10+" },
    { tier: 12, min: 57, max: 61,  label: "Late Fliers" },
    { tier: 13, min: 62, max: 999, label: "Deep / Waiver" },
  ],
  TE: [
    { tier: 1, min: 1,  max: 2,   label: "Elite" },
    { tier: 2, min: 3,  max: 8,   label: "Rounds 3-7" },
    { tier: 3, min: 9,  max: 11,  label: "Rounds 8-10" },
    { tier: 4, min: 12, max: 15,  label: "Rounds 10-14" },
    { tier: 5, min: 16, max: 23,  label: "Late Fliers" },
    { tier: 6, min: 24, max: 32,  label: "Streamers" },
    { tier: 7, min: 33, max: 999, label: "Deep Bench" },
  ],
  DEF: [
    { tier: 1, min: 1, max: 3,   label: "Elite DEF" },
    { tier: 2, min: 4, max: 8,   label: "Solid DEF" },
    { tier: 3, min: 9, max: 999, label: "Streamable" },
  ],
  K: [
    { tier: 1, min: 1, max: 2,   label: "Top K" },
    { tier: 2, min: 3, max: 5,   label: "Solid K" },
    { tier: 3, min: 6, max: 999, label: "Streamable" },
  ],
};

export function getDisplayTierForRank(pos, rankNum) {
  const tiers = DISPLAY_TIER_MAPS[pos];
  if (!tiers) return null;
  for (const t of tiers) {
    if (rankNum >= t.min && rankNum <= t.max) return t;
  }
  return tiers[tiers.length - 1];
}

// ---------------------------------------------------------------------------
// Per-league display-tier customization (moves / order / names) + composition.
//
// These are the SHARED readers so the Tier Builder and the in-draft TierViewer
// render the SAME organizational tiers. The Tier Builder persists to its OWN
// localStorage keys (below) — separate from userTierSheet / userTierOrder /
// userTierNames, which still feed the price engine and AI sheets. Keeping the
// keys separate is what guarantees display tiering never leaks into $ values.
// ---------------------------------------------------------------------------

export function lsKey(base, leagueName) {
  return base + "_" + (leagueName || "default");
}

export const LS_MOVES = "userDisplayTierMoves"; // { "POS:Name": displayTierNum }
export const LS_ORDER = "userDisplayTierOrder"; // { "POS:tier": [name, ...] }
export const LS_NAMES = "userDisplayTierNames"; // { "POS:tier": customLabel }

export function loadOverrides(leagueName) {
  try {
    const raw = localStorage.getItem(lsKey(LS_MOVES, leagueName));
    if (!raw) return {};
    const pairs = JSON.parse(raw);
    if (Array.isArray(pairs)) {
      const obj = {};
      pairs.forEach(([k, v]) => { obj[k] = v; });
      return obj;
    }
    if (typeof pairs === "object" && pairs !== null) return pairs;
  } catch { /* ignore */ }
  return {};
}

export function loadTierOrder(leagueName) {
  try {
    const raw = localStorage.getItem(lsKey(LS_ORDER, leagueName));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) return parsed;
  } catch { /* ignore */ }
  return {};
}

export function loadTierNames(leagueName) {
  try {
    const raw = localStorage.getItem(lsKey(LS_NAMES, leagueName));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) return parsed;
  } catch { /* ignore */ }
  return {};
}

// WHICH DISPLAY TIER IS THIS PLAYER IN — one resolution, shared.
//
// The expert band from DISPLAY_TIER_MAPS, with a manual move layered on top and
// clamped to a real tier so no player can be lost off either end.
//
// ⚠️ THE BADGE AND THE BOARD MUST AGREE. buildPosTierData below (the Tier
// Builder / TierViewer grouping) and PlayerList's Team Builder tier badge both
// call this. They used to answer the question differently — the badge read the
// dead `userTierOverrides` price key and so ignored every drag — and a badge
// that contradicts the board the user just dragged on is the same class of lie
// the display/price split exists to prevent. One function, two callers.
//
// Display-only, like everything else in this file: never feeds getTierVal,
// generatePersonalTierSheet, usePricing, AI tier sheets or batch sim.
export function resolveDisplayTier(pos, playerName, rankNum, moves = {}) {
  const tiers = DISPLAY_TIER_MAPS[pos];
  if (!tiers) return null;
  const tierObj = getDisplayTierForRank(pos, rankNum);
  const autoTier = tierObj ? tierObj.tier : tiers[tiers.length - 1].tier;
  const moved = moves?.[`${pos}:${playerName}`];
  return Math.max(1, Math.min(tiers.length, moved ?? autoTier));
}

// Build display-tier data for a single position: the expert DISPLAY_TIER_MAPS
// bands (round-oriented labels), with manual moves layered on top. moves =
// { "POS:Name": tierNum }; tierOrder = within-tier order. Grouping only — still
// fully decoupled from pricing (persists to userDisplayTier* keys, never
// userTierSheet), so it never feeds getTierVal / usePricing / the AI sheets.
export function buildPosTierData(players, pos, moves = {}, tierOrder) {
  const tiers = DISPLAY_TIER_MAPS[pos];
  if (!tiers) return { groups: {}, pMap: {}, tiers: [] };

  const pMap = {};
  const groups = {};
  tiers.forEach(t => { groups[t.tier] = []; });

  players
    .filter(p => p.pos === pos)
    .forEach(p => {
      const rankStr = getEffectivePosRank(p.name, p.posRank);
      const rankNum = parseRankNum(rankStr);
      pMap[p.name] = { ...p, rankNum, rankStr };

      // Band + manual move + clamp — see resolveDisplayTier above. Shared with
      // the Team Builder tier badge so the two cannot disagree.
      const assignedTier = resolveDisplayTier(pos, p.name, rankNum, moves);

      if (!groups[assignedTier]) groups[assignedTier] = [];
      groups[assignedTier].push(p.name);
    });

  // Apply custom ordering or default ADP sort per tier
  Object.keys(groups).forEach(t => {
    const orderKey = `${pos}:${t}`;
    const customOrder = tierOrder?.[orderKey];
    if (customOrder && customOrder.length > 0) {
      // Sort by position in custom order array; new players (not in order) go to end sorted by ADP
      const orderIndex = new Map(customOrder.map((name, i) => [name, i]));
      groups[t].sort((a, b) => {
        const ai = orderIndex.has(a) ? orderIndex.get(a) : 99999;
        const bi = orderIndex.has(b) ? orderIndex.get(b) : 99999;
        if (ai !== 99999 || bi !== 99999) return ai - bi;
        return (pMap[a]?.rankNum || 999) - (pMap[b]?.rankNum || 999);
      });
    } else {
      groups[t].sort((a, b) => (pMap[a]?.rankNum || 999) - (pMap[b]?.rankNum || 999));
    }
  });

  return { groups, pMap, tiers };
}
